/**
 * PWA Utilities
 *
 * Helper functions for detecting and handling Progressive Web App context
 */
import { createLogger } from "./logger";
import {
  debugSSR,
  isBrowser,
  safeDocument,
  safeLocalStorage,
  safeNavigator,
  safeWindow,
} from "./ssr-guards";

const logger = createLogger("pwa");

/**
 * Detects if the app is running as an installed PWA
 *
 * Works for:
 * - Android/Chrome: display-mode: standalone
 * - iOS/Safari: navigator.standalone
 *
 * @returns true if running as installed PWA, false otherwise
 */
export function isPWA(): boolean {
  if (!isBrowser()) {
    debugSSR("isPWA() called during SSR, returning false");
    return false;
  }

  const window = safeWindow();
  const navigator = safeNavigator();

  if (!window || !navigator) {
    debugSSR("isPWA() - window or navigator not available");
    return false;
  }

  // Check for standalone display mode (Android/Chrome)
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  // Check for iOS standalone mode
  const isIOSStandalone = (navigator as any).standalone === true;

  return isStandalone || isIOSStandalone;
}

/**
 * Gets the current display mode
 *
 * @returns 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser'
 */
export function getDisplayMode(): "standalone" | "fullscreen" | "minimal-ui" | "browser" {
  if (typeof window === "undefined") {
    return "browser";
  }

  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  return "browser";
}

/**
 * Checks if the app is installable (has service worker and manifest)
 *
 * @returns true if PWA is installable
 */
export function isPWAInstallable(): boolean {
  if (!isBrowser()) {
    debugSSR("isPWAInstallable() called during SSR, returning false");
    return false;
  }

  const navigator = safeNavigator();
  const document = safeDocument();

  if (!navigator || !document) {
    debugSSR("isPWAInstallable() - navigator or document not available");
    return false;
  }

  // Check for service worker support
  const hasServiceWorker = "serviceWorker" in navigator;

  // Check for manifest - document is already guarded by safeDocument()
  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;

  return hasServiceWorker && hasManifest;
}

/**
 * Gets PWA installation status
 *
 * @returns Object with installation details
 */
export function getPWAStatus() {
  return {
    isPWA: isPWA(),
    displayMode: getDisplayMode(),
    isInstallable: isPWAInstallable(),
    platform: getPlatform(),
  };
}

/**
 * Detects the platform the PWA is running on
 *
 * @returns 'ios' | 'android' | 'desktop' | 'unknown'
 */
export function getPlatform(): "ios" | "android" | "desktop" | "unknown" {
  if (typeof window === "undefined") {
    return "unknown";
  }

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }
  if (/android/.test(userAgent)) {
    return "android";
  }
  if (/windows|mac|linux/.test(userAgent)) {
    return "desktop";
  }
  return "unknown";
}

/**
 * Adds PWA context to analytics events
 *
 * @returns Object with PWA properties for analytics
 */
export function getPWAAnalyticsContext() {
  const status = getPWAStatus();

  return {
    is_pwa: status.isPWA,
    display_mode: status.displayMode,
    platform: status.platform,
    is_installable: status.isInstallable,
  };
}

/**
 * Checks if the app should show install prompt
 *
 * @returns true if install prompt should be shown
 */
export function shouldShowInstallPrompt(): boolean {
  // Don't show if already installed
  if (isPWA()) {
    return false;
  }

  // Don't show if not installable
  if (!isPWAInstallable()) {
    return false;
  }

  // Check if user has dismissed install prompt before
  const localStorage = safeLocalStorage();
  if (localStorage) {
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);

      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Marks install prompt as dismissed
 */
export function dismissInstallPrompt(): void {
  const localStorage = safeLocalStorage();
  if (localStorage) {
    localStorage.setItem("pwa-install-dismissed", new Date().toISOString());
  } else {
    debugSSR("dismissInstallPrompt() called during SSR, ignoring");
  }
}

/**
 * Logs PWA status to console (development only)
 */
export function logPWAStatus(): void {
  if (process.env.NODE_ENV === "development") {
    const status = getPWAStatus();
    logger.debug("PWA Status", status);
  }
}
